// Formulario de búsqueda avanzada del catálogo (Req. 3.2–3.9).
//
// Campos AGRUPADOS: el grupo "Principales" siempre visible; los secundarios en
// grupos plegables. Cada campo usa el control según su tipo (texto / lista /
// rango / booleano / typeahead / combo). Construye un objeto `criteria` que
// combina con AND solo los criterios presentes (el backend ignora los vacíos).
// Botón "Limpiar" resetea.
//
// Mejoras de ingreso de datos (Req. 3.1.a, 3.5.a):
//   - Campos de texto clave ofrecen SUGERENCIAS tras ≥3 caracteres (typeahead).
//   - El género usa un DESPLEGABLE FILTRABLE (combo) que consume /genres y filtra
//     por `genre_id`.
//   - Nuevo grupo "Sincronización": estado de sync con Discogs + requiere validación.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import type { GenreOption, SearchCriteria } from '../api/endpoints.js';
import { Collapsible, Icon } from '../ui/components.js';

type FieldType = 'text' | 'list' | 'range' | 'boolean' | 'typeahead' | 'genre';

interface FieldDef {
  key: string;
  /** Clave i18n del rótulo del campo. */
  labelKey: string;
  type: FieldType;
  /** Opciones para el tipo lista (valores que se envían al backend). */
  options?: string[];
  /**
   * Claves i18n de etiqueta por opción (valor → clave). Si falta, se muestra el
   * propio valor. Permite enviar el valor del enum del backend (ej. `synced`)
   * mostrando un texto traducido.
   */
  optionLabelKeys?: globalThis.Record<string, string>;
  /** Para typeahead: campo del catálogo a sugerir (artist/title/label/...). */
  suggestField?: string;
}

interface GroupDef {
  titleKey: string;
  fields: FieldDef[];
}

/** Grupos y campos (alineados con la lista blanca del backend, Req. 3.2). */
const GROUPS: GroupDef[] = [
  {
    titleKey: 'advSearch.groupMain',
    fields: [
      { key: 'artist', labelKey: 'advSearch.artist', type: 'typeahead', suggestField: 'artist' },
      { key: 'title', labelKey: 'advSearch.title', type: 'typeahead', suggestField: 'title' },
      { key: 'label_catalog_nr', labelKey: 'advSearch.catalogNr', type: 'typeahead', suggestField: 'label_catalog_nr' },
      { key: 'type', labelKey: 'advSearch.type', type: 'list', options: ['Album', 'Single', 'Soundtrack', 'EP', 'Other'] },
      { key: 'genre_id', labelKey: 'advSearch.genre', type: 'genre' },
      { key: 'subgenre', labelKey: 'advSearch.subgenre', type: 'typeahead', suggestField: 'subgenre' },
      { key: 'discogs_genre', labelKey: 'advSearch.discogsGenre', type: 'typeahead', suggestField: 'discogs_genre' },
      { key: 'discogs_styles', labelKey: 'advSearch.discogsStyles', type: 'typeahead', suggestField: 'discogs_styles' },
    ],
  },
  {
    titleKey: 'advSearch.groupEdition',
    fields: [
      { key: 'origin_country', labelKey: 'advSearch.origin', type: 'list', options: ['US', 'UK', 'COL', 'EU', 'JP', 'DE', 'FR'] },
      { key: 'size_inches', labelKey: 'advSearch.size', type: 'list', options: ['12', '10', '7'] },
      { key: 'original_release_year', labelKey: 'advSearch.releaseYear', type: 'range' },
      { key: 'edition_year', labelKey: 'advSearch.editionYear', type: 'range' },
      { key: 'vinyl_color', labelKey: 'advSearch.color', type: 'typeahead', suggestField: 'vinyl_color' },
      { key: 'label', labelKey: 'advSearch.label', type: 'typeahead', suggestField: 'label' },
      { key: 'commercial_value', labelKey: 'advSearch.commercialValue', type: 'range' },
    ],
  },
  {
    titleKey: 'advSearch.groupCondition',
    fields: [
      { key: 'media_condition', labelKey: 'advSearch.mediaCondition', type: 'list', options: ['M', 'NM', 'VG+', 'VG', 'G+', 'G', 'P'] },
      { key: 'sleeve_condition', labelKey: 'advSearch.sleeveCondition', type: 'list', options: ['M', 'NM', 'VG+', 'VG', 'G+', 'G', 'P'] },
    ],
  },
  {
    titleKey: 'advSearch.groupSync',
    fields: [
      {
        key: 'sync_status',
        labelKey: 'advSearch.syncStatus',
        type: 'list',
        options: ['pending', 'synced', 'skipped', 'failed'],
        optionLabelKeys: {
          pending: 'advSearch.syncPending',
          synced: 'advSearch.syncSynced',
          skipped: 'advSearch.syncSkipped',
          failed: 'advSearch.syncFailed',
        },
      },
      { key: 'needs_review', labelKey: 'advSearch.needsReview', type: 'boolean' },
    ],
  },
];

export function AdvancedSearchForm({
  criteria,
  onChange,
  onSearch,
  onClear,
  title,
  icon = 'tune',
  defaultCollapsed = false,
  collapsible = true,
}: {
  criteria: SearchCriteria;
  onChange: (c: SearchCriteria) => void;
  onSearch: () => void;
  onClear: () => void;
  /** Título del panel que se muestra en la cabecera plegable. */
  title?: string;
  /** Icono (Material Symbols) junto al título. */
  icon?: string;
  /** Si el panel arranca plegado. */
  defaultCollapsed?: boolean;
  /** Permite desactivar el plegado (muestra siempre el cuerpo). */
  collapsible?: boolean;
}) {
  const { t } = useTranslation();
  const panelTitle = title ?? t('catalog.advancedSearch');
  // El grupo "Principales" arranca abierto; los secundarios, plegados.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const first = GROUPS[0];
    return first ? new Set([first.titleKey]) : new Set();
  });
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const activeCount = Object.keys(criteria).length;

  function setValue(key: string, value: unknown) {
    const next = { ...criteria };
    if (value === undefined || value === '' || value === null) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  }

  function setRange(key: string, part: 'min' | 'max', value: string) {
    const current = (criteria[key] as { min?: number; max?: number } | undefined) ?? {};
    const next = { ...current };
    if (value === '') delete next[part];
    else next[part] = Number(value);
    if (next.min === undefined && next.max === undefined) setValue(key, undefined);
    else setValue(key, next);
  }

  function renderField(f: FieldDef) {
    const value = criteria[f.key];
    const fieldLabelText = t(f.labelKey);
    if (f.type === 'typeahead') {
      return (
        <TypeaheadField
          key={f.key}
          label={fieldLabelText}
          field={f.suggestField ?? f.key}
          value={(value as string) ?? ''}
          onChange={(v) => setValue(f.key, v)}
        />
      );
    }
    if (f.type === 'genre') {
      return (
        <GenreField
          key={f.key}
          label={fieldLabelText}
          value={(value as string) ?? ''}
          onChange={(id) => setValue(f.key, id)}
        />
      );
    }
    if (f.type === 'text') {
      return (
        <div className="flex flex-col gap-space-xs" key={f.key}>
          <label className="field-label">{fieldLabelText}</label>
          <div className="field-slot">
            <input className="field-input" value={(value as string) ?? ''} onChange={(e) => setValue(f.key, e.target.value)} />
          </div>
        </div>
      );
    }
    if (f.type === 'list') {
      const currentListValue = (value as string) ?? '';
      return (
        <div className="flex flex-col gap-space-xs" key={f.key}>
          <label className="field-label">{fieldLabelText}</label>
          <div className="field-slot">
            <select
              className="field-input"
              value={currentListValue}
              onChange={(e) => setValue(f.key, e.target.value)}
            >
              <option value="">{t('advSearch.any')}</option>
              {f.options?.map((o) => (
                <option key={o} value={o}>
                  {f.optionLabelKeys?.[o] ? t(f.optionLabelKeys[o]!) : o}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    }
    if (f.type === 'boolean') {
      const current = value === true ? 'true' : value === false ? 'false' : '';
      return (
        <div className="flex flex-col gap-space-xs" key={f.key}>
          <label className="field-label">{fieldLabelText}</label>
          <div className="field-slot">
            <select className="field-input" value={current} onChange={(e) => setValue(f.key, e.target.value === '' ? undefined : e.target.value === 'true')}>
              <option value="">{t('advSearch.any')}</option>
              <option value="true">{t('advSearch.yes')}</option>
              <option value="false">{t('advSearch.no')}</option>
            </select>
          </div>
        </div>
      );
    }
    // range
    const range = (value as { min?: number; max?: number } | undefined) ?? {};
    return (
      <div className="flex flex-col gap-space-xs" key={f.key}>
        <label className="field-label">{fieldLabelText} {t('advSearch.rangeSuffix')}</label>
        <div className="flex items-center gap-space-xs">
          <div className="field-slot flex-1">
            <input className="field-input" type="number" placeholder={t('advSearch.min')} value={range.min ?? ''}
              onChange={(e) => setRange(f.key, 'min', e.target.value)} />
          </div>
          <div className="field-slot flex-1">
            <input className="field-input" type="number" placeholder={t('advSearch.max')} value={range.max ?? ''}
              onChange={(e) => setRange(f.key, 'max', e.target.value)} />
          </div>
        </div>
      </div>
    );
  }

  const body = (
    <form className="flex flex-col gap-space-sm" onSubmit={(e) => { e.preventDefault(); onSearch(); }}>
      {GROUPS.map((group) => {
        const open = openGroups.has(group.titleKey);
        return (
          <Collapsible
            key={group.titleKey}
            title={t(group.titleKey)}
            open={open}
            onToggle={() =>
              setOpenGroups((prev) => {
                const next = new Set(prev);
                if (next.has(group.titleKey)) next.delete(group.titleKey);
                else next.add(group.titleKey);
                return next;
              })
            }
          >
            <div className="grid grid-cols-2 gap-space-sm">{group.fields.map(renderField)}</div>
          </Collapsible>
        );
      })}

      <div className="flex items-center gap-space-sm">
        <button className="btn-secondary" type="button" onClick={onClear}>{t('advSearch.clear')}</button>
        <button className="btn-primary flex-1" type="submit">{t('advSearch.search')}</button>
      </div>
      <span className="font-body-sm text-body-sm text-on-surface-variant">
        {t('advSearch.activeCriteria', { count: activeCount })}
      </span>
    </form>
  );

  // Sin plegado: comportamiento clásico (cuerpo siempre visible).
  if (!collapsible) return body;

  return (
    <div className="accordion">
      <button
        type="button"
        className="accordion-header"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <span className="flex items-center gap-space-xs">
          <Icon name={icon} className="text-primary text-[20px]" />
          {panelTitle}
        </span>
        <span className="flex items-center gap-space-sm">
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary-container/30 text-primary font-body-sm text-body-sm">
              {activeCount}
            </span>
          )}
          <Icon
            name="expand_more"
            className={`text-[22px] text-on-surface-variant transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
          />
        </span>
      </button>
      {!collapsed && <div className="p-space-md border-t border-border-subtle">{body}</div>}
    </div>
  );
}

/**
 * Campo de texto con autocompletar (Req. 3.1.a): tras ≥3 caracteres, consulta
 * sugerencias del catálogo (con debounce) y las ofrece en una lista. Elegir una
 * fija el valor; también se puede seguir escribiendo texto libre.
 */
function TypeaheadField({
  label,
  field,
  value,
  onChange,
}: {
  label: string;
  field: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const api = useApi();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = value.trim();
    if (!focused || term.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await api.records.suggest(field, term);
        if (cancelled) return;
        // No sugerir un único valor idéntico a lo ya escrito.
        const items = res.items.filter((s) => s.toLowerCase() !== term.toLowerCase());
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setOpen(false);
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [api, field, value, focused]);

  return (
    <div className="flex flex-col gap-space-xs relative" ref={boxRef}>
      <label className="field-label">{label}</label>
      <div className="field-slot">
        <input
          className="field-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            // Retardo para permitir el click en una sugerencia antes de cerrar.
            setTimeout(() => setOpen(false), 150);
          }}
          autoComplete="off"
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 top-full left-0 right-0 mt-1 max-h-48 overflow-auto rounded-lg bg-surface-container-high border border-border-subtle shadow-lg">
          {suggestions.map((s) => (
            <li key={s}>
              <button
                type="button"
                className="w-full text-left px-space-sm py-2 font-body-sm text-body-sm text-on-surface hover:bg-surface-rack"
                onMouseDown={(e) => {
                  // onMouseDown (antes de blur) para que el valor se fije.
                  e.preventDefault();
                  onChange(s);
                  setOpen(false);
                }}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Desplegable FILTRABLE de género (Req. 3.5.a): carga los géneros del catálogo
 * y permite encontrarlos escribiendo texto. Al elegir uno, fija el `genre_id`
 * (el backend filtra por `genre_id` como lista de UUID).
 */
function GenreField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (id: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [genres, setGenres] = useState<GenreOption[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Solo la taxonomía PROPIA: el campo filtra por `genre_id` (FK de los discos
    // del tenant, que apunta a géneros 'propio'). Los géneros 'discogs' son
    // valores de referencia para el mapeo del agente, no sirven para filtrar el
    // catálogo y confundían al aparecer aquí (coherente con GenreTaxonomyView).
    api.genres
      .list('propio')
      .then((res) => {
        if (!cancelled) setGenres(res.items);
      })
      .catch(() => {
        if (!cancelled) setGenres([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const selectedName = useMemo(
    () => genres.find((g) => g.id === value)?.name ?? '',
    [genres, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q === '' ? genres : genres.filter((g) => g.name.toLowerCase().includes(q));
    return base.slice(0, 30);
  }, [genres, query]);

  return (
    <div className="flex flex-col gap-space-xs relative">
      <label className="field-label">{label}</label>
      <div className="field-slot">
        <Icon name="search" className="text-on-surface-variant text-[16px] mr-space-xs" />
        <input
          className="field-input"
          placeholder={selectedName || t('advSearch.any')}
          value={open ? query : selectedName}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setQuery('');
            setOpen(true);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            aria-label={t('advSearch.removeGenre')}
            className="w-7 h-7 shrink-0 rounded text-on-surface-variant flex items-center justify-center hover:text-on-surface"
            onMouseDown={(e) => {
              e.preventDefault();
              onChange(undefined);
              setQuery('');
            }}
          >
            <Icon name="close" className="text-[16px]" />
          </button>
        )}
      </div>
      {open && (
        <ul className="absolute z-20 top-full left-0 right-0 mt-1 max-h-48 overflow-auto rounded-lg bg-surface-container-high border border-border-subtle shadow-lg">
          {filtered.length === 0 ? (
            <li className="px-space-sm py-2 font-body-sm text-body-sm text-on-surface-variant">
              {t('advSearch.noMatches')}
            </li>
          ) : (
            filtered.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  className="w-full text-left px-space-sm py-2 font-body-sm text-body-sm text-on-surface hover:bg-surface-rack"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(g.id);
                    setOpen(false);
                  }}
                >
                  {g.name}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
